import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Text, Link } from 'rebass/styled-components'

import { isValidDate } from '../../../utils/date'
import { ChevronRightIcon } from '../../Icon'
import { MenuLinks, MenuContent, MenuSection } from '../Menu'
import { formattedDate } from '../../../utils/date'
import type { ChangelogEntry, MenuLink } from './index'

const generateMenuLinkItems = (entries: ChangelogEntry[], lastViewedDate?: string): MenuLink[] => {
  const menuLinks = entries.map(entry => ({
    description: formattedDate(entry.created_at),
    forceRefresh: true,
    isUnread: lastViewedDate ? entry.created_at > lastViewedDate : true,
    name: entry.title,
    to: entry.url
  }))
  return menuLinks
}

interface DropdownProps {
  changelogViewDate: string
  entries?: ChangelogEntry[]
  isExpanded: boolean
  loading: boolean
  setChangelogViewDate: React.Dispatch<React.SetStateAction<string>>
}

export const Dropdown = ({
  changelogViewDate,
  entries,
  isExpanded,
  loading,
  setChangelogViewDate
}: DropdownProps): JSX.Element => {
  const [menuLinks, setMenuLinks] = useState<MenuLink[]>([])
  useEffect(() => {
    // Grab the last viewed date from local storage; set to undefined if not a valid date
    const lastViewedDate = isValidDate(new Date(changelogViewDate)) ? changelogViewDate : undefined
    entries && setMenuLinks(generateMenuLinkItems(entries, lastViewedDate))
    return () => {
      // Set the last viewed date to the current date when the menu is closed
      // Grab current date in the same format of the changelog entry 'created_at' date
      const now = new Date().toISOString().split('T')[0]
      // Only run updates if lastViewedDate has not been set or is prior to the current date
      if (!lastViewedDate || now > lastViewedDate) {
        entries && setMenuLinks(generateMenuLinkItems(entries, now))
        setChangelogViewDate(now)
      }
    }
  }, [isExpanded])

  const { formatMessage } = useIntl()

  return (
    <MenuContent level="sm">
      {/* @ts-ignore */}
      <MenuSection>
        <MenuLinks
          hasMore={undefined}
          hasUnread={menuLinks.some(link => link.isUnread)}
          links={menuLinks}
          loading={loading}
          maxHeight="100%"
          onLoadMore={undefined}
          title={formatMessage({ id: 'navigation.menu.changelog.title' })}
        />
      </MenuSection>
      <MenuSection>
        <Text as="span">
          <Link variant="links.base" href="/changelog" target="_blank">
            <FormattedMessage id="navigation.menu.changelog.viewAll" />
            <ChevronRightIcon color="inherit" width={5} height={10} sx={{ ml: '10px' }} />
          </Link>
        </Text>
      </MenuSection>
    </MenuContent>
  )
}
